import React from "react";
import formStyles from "@/components/DynamicForm/index.module.less";
// import { DownloadOutlined } from "@ant-design/icons";
import { Button, Upload, Form } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd";
import BankInfoTable from "@/pages/supplier/SupplierProfile/components/RegisteredTab/components/BankInfoTable";
import type { FormInstance } from "antd";
import { srmUploadService } from "@/utils/upload";
import { notifyError, notifySuccess } from "@/utils/notification";
import { FileListItem } from "@/components/FileListItem/FileListItem";
import { uploadSupplierFlowBusinessFiles } from "@/apis/flow.api";
import { LANGUAGE_OPTIONS } from "@/pages/supplier/SupplierProfile/components/RegisteredTab/config/data";

// ==================== 类型定义 ====================

/**
 * 注册人信息数据结构
 */
interface RegistrantInfo {
  registrantId?: number;
  supplierId?: number;
  surname?: string; // 姓氏
  name?: string; // 名字
  phone?: string; // 电话
  deptCode?: string; // 部门代码
  email?: string; // 邮箱
  status?: number; // 状态
}

/**
 * 银行信息数据结构（后端返回 + 前端使用）
 */
interface BankInfo {
  id?: string; // 新增：唯一标识
  accountType?: string; // 账户类型
  country?: string; // 银行国家
  openBankName?: string; // 开户行名称
  openBankNumber?: string; // 开户行行号
  account?: string; // 银行账号
  swiftCode?: string; // SWIFT代码
  accountHolder?: string; // 开户人/单位
  openBankInSap?: number; // 是否在SAP中
}

/**
 * 档案/证照信息数据结构
 */
interface ProfileInfo {
  fileType?: number | string; // 文件类型：0-营业执照，1-银行信息，2-收款授权信，3-开户证明，4-其他 或 对应的标签名称
  fileName?: string; // 文件名称
  fileUrl?: string; // 文件URL（逗号隔开的多个文件）
  fileSize?: string; // 文件大小
  id?: string; // 文件ID
  showTip?: boolean; // 是否显示提示
  fileTip?: string; // 文件提示信息
  [key: string]: any;
}

/**
 * 单个文件项数据结构
 */
interface FileItem {
  name: string; // 文件名
  url: string; // 文件URL
  index: number; // 文件序号
}

/**
 * 文件类型映射关系
 * 用于将 fileType 数字转换为显示文本和表单字段名
 */
const FILE_TYPE_MAP: Record<number, { label: string; fieldName: string }> = {
  0: { label: "营业执照", fieldName: "businessLicense" },
  1: { label: "银行信息", fieldName: "bankDocs" },
  2: { label: "收款授权信", fieldName: "paymentAuthorizationLetter" },
  3: { label: "开户证明", fieldName: "bankOpeningProof" },
  4: { label: "其他", fieldName: "otherDocs" },
} as const;

/**
 * 反向映射：从标签文本获取对应的数字类型
 * 用于处理后端返回 fileType 为字符串的情况
 *
 * 注意：进行了大小写和空格的标准化处理，确保匹配准确
 */
const LABEL_TO_TYPE_MAP: Record<string, number> = {
  营业执照: 0,
  银行信息: 1,
  收款授权信: 2,
  开户证明: 3,
  其他: 4,
} as const;

/**
 * 将标签字符串规范化，处理空格和大小写问题
 * @param label 原始标签字符串
 * @returns 规范化后的标签字符串
 */
const normalizeLabel = (label: string): string => {
  return label.trim().toLowerCase();
};

/**
 * 创建规范化的反向映射表（用于字符串匹配）
 */
const createNormalizedLabelMap = (): Record<string, number> => {
  const map: Record<string, number> = {};
  Object.entries(LABEL_TO_TYPE_MAP).forEach(([label, type]) => {
    map[normalizeLabel(label)] = type;
  });
  return map;
};

const NORMALIZED_LABEL_TO_TYPE_MAP = createNormalizedLabelMap();

/**
 * 文件上传配置（基础配置，不包含customRequest）
 * customRequest 将在 ProfileFileFormField 组件中根据文件类型进行自定义
 */
const uploadProps: UploadProps = {
  name: "file",
  showUploadList: false,
  accept: ".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx",
  multiple: true, // 支持多文件选择
  maxCount: 10,
};

// ==================== 辅助函数 ====================

/**
 * 将 fileType 转换为数字类型
 * 支持数字和字符串两种格式，并进行规范化处理确保准确匹配
 *
 * @param fileType 文件类型（数字或数字字符串"0"-"4"或标签字符串）
 * @returns 对应的数字类型（0-4），如果无效则返回 4（其他）
 */
const normalizeFileType = (fileType?: number | string): number => {
  // 如果是数字，直接返回
  if (typeof fileType === "number") {
    return fileType;
  }

  // 如果是字符串，进行规范化并查询映射表
  if (typeof fileType === "string") {
    // 首先尝试将字符串转换为数字（处理"0", "1", "2", "3", "4"这样的数字字符串）
    const numericValue = parseInt(fileType, 10);
    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 4) {
      return numericValue;
    }

    // 再尝试精确匹配（中文标签）
    const exactMatch = LABEL_TO_TYPE_MAP[fileType];
    if (exactMatch !== undefined) {
      return exactMatch;
    }

    // 再尝试规范化后匹配（处理空格、大小写等）
    const normalizedLabel = normalizeLabel(fileType);
    const normalizedMatch = NORMALIZED_LABEL_TO_TYPE_MAP[normalizedLabel];
    if (normalizedMatch !== undefined) {
      return normalizedMatch;
    }
    return 4;
  }

  // 默认为"其他"
  return 4;
};

/**
 * 根据 fileType 和 fileUrl 自动生成 fileName
 * 如果 fileName 为空，则使用 fileType 标签 + 文件URL中的文件名
 *
 * @param profile 档案/证照信息
 * @returns 补全后的档案信息
 */
const enrichProfileInfo = (profile: ProfileInfo): ProfileInfo => {
  const fileType = normalizeFileType(profile.fileType);
  const fileTypeLabel = FILE_TYPE_MAP[fileType]?.label || "其他";

  // 如果 fileName 不存在，则生成一个
  if (!profile.fileName && profile.fileUrl) {
    // 从 fileUrl 中提取文件名
    const urlFileName =
      profile.fileUrl.split("/").pop()?.split("?")[0] || "未知文件";
    profile.fileName = `${fileTypeLabel}-${urlFileName}`;
  } else if (!profile.fileName) {
    // 如果 fileUrl 也不存在，则仅使用 fileType 标签
    profile.fileName = fileTypeLabel;
  }

  return profile;
};

/**
 * 文件上传变化处理函数
 * 处理多文件上传的状态更新和通知
 *
 * 优化：不在此处调用通知，由customRequest中的批量API调用来处理
 */
const onFileChange = (fieldName: string, form: FormInstance) => (info: any) => {
  // 仅更新表单值，移除通知逻辑（通知由批量上传API处理）
  if (info.fileList && info.fileList.length > 0) {
    form.setFieldsValue({
      [fieldName]: info.fileList.map((f: any) => ({
        ...f,
        url: f.response?.url || f.url,
        fileId: f.response?.fileId, // 保留从customRequest返回的fileId
      })),
    });
  }

  // 处理上传错误
  const errorFiles = info.fileList.filter((f: any) => f.status === "error");
  if (errorFiles.length > 0) {
    errorFiles.forEach((errorFile: any) => {
      notifyError(`${errorFile.name} ${t("文件上传失败")}`);
    });
  }
};

/**
 * 从逗号隔开的文件URL字符串中解析单个文件对象数组
 * 支持文件名提取：如果URL是文件路径格式，则从中提取文件名
 *
 * 示例：
 * - 输入: "http://example.com/file1.pdf,http://example.com/file2.doc"
 * - 输出: [
 *     { name: "file1.pdf", url: "http://example.com/file1.pdf", index: 0 },
 *     { name: "file2.doc", url: "http://example.com/file2.doc", index: 1 }
 *   ]
 *
 * @param fileUrlString 逗号隔开的文件URL字符串
 * @returns 文件对象数组
 */
const parseFileUrls = (fileUrlString?: string): FileItem[] => {
  if (!fileUrlString || typeof fileUrlString !== "string") {
    return [];
  }

  return fileUrlString
    .split(",")
    .map((url, index) => {
      const trimmedUrl = url.trim();
      // 从URL中提取文件名（如果是完整路径，取最后一部分；如果是简单名称，直接使用）
      const fileName = trimmedUrl.includes("/")
        ? trimmedUrl.split("/").pop() || `文件${index + 1}`
        : trimmedUrl || `文件${index + 1}`;

      return {
        name: fileName,
        url: trimmedUrl,
        index,
      };
    })
    .filter((item) => item.url); // 过滤掉空URL
};

/**
 * 个别文件上传表单项组件
 * 使用 Ant Design Upload 组件处理文件上传
 */
interface ProfileFileFormFieldProps {
  fieldName: string;
  label: string;
  initialFiles?: FileItem[];
  required?: boolean;
  readOnly?: boolean;
  t?: any; // 翻译函数
}

const ProfileFileFormField: React.FC<ProfileFileFormFieldProps> = ({
  fieldName,
  label,
  initialFiles = [],
  required = true,
  readOnly = false,
  t = (key: string) => key,
}) => {
  const form = Form.useFormInstance();
  // 用于收集待上传的文件，实现批量上传
  const uploadQueueRef = React.useRef<
    Array<{ url: string; fileName: string; onSuccess: Function }>
  >([]);
  const apiCallTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const totalFilesCountRef = React.useRef<number>(0);
  const completedFilesCountRef = React.useRef<number>(0);

  /**
   * 批量调用API上传文件信息（所有文件在一个请求中）
   */
  const batchUploadFilesToAPI = React.useCallback(
    async (
      filesToUpload: Array<{
        url: string;
        fileName: string;
        onSuccess: Function;
      }>,
    ) => {
      if (filesToUpload.length === 0) return;

      try {
        // 获取文件类型对应的数字
        const fileTypeNumber = LABEL_TO_TYPE_MAP[label] ?? 4;

        // 准备批量上传的数据
        const fileDataList = filesToUpload.map((file) => ({
          fileUrl: file.url,
          fileName: file.fileName,
          fileType: fileTypeNumber,
        }));

        // 一次API调用，传所有文件数据
        const response = await uploadSupplierFlowBusinessFiles(fileDataList);

        // 处理响应：返回的可能是数组或单个对象
        const fileIds = Array.isArray(response?.data?.data)
          ? response?.data?.data
          : response?.data?.data
            ? [response?.data?.data]
            : [];

        // 为每个文件调用对应的onSuccess，传递fileId
        filesToUpload.forEach((file, index) => {
          const fileId = fileIds[index];
          file.onSuccess?.({ url: file.url, fileId });
        });

        notifySuccess(
          `${t("成功批量上传")}${filesToUpload.length}${t("个文件")}`,
        );
      } catch (apiError: any) {
        // API调用失败时，仍然为所有文件调用onSuccess，因为文件已上传到OSS
        filesToUpload.forEach((file) => {
          file.onSuccess?.({ url: file.url });
        });
        notifyError(t("文件信息记录失败，但文件已成功上传到OSS"));
      }
    },
    [label, t],
  );

  /**
   * 检查是否所有文件都已准备好，如果是则执行批量上传
   */
  const checkAndExecuteBatchUpload = React.useCallback(() => {
    completedFilesCountRef.current++;

    // 当所有文件都完成OSS上传时，执行一次API调用
    if (
      completedFilesCountRef.current >= totalFilesCountRef.current &&
      uploadQueueRef.current.length > 0
    ) {
      const filesToUpload = uploadQueueRef.current;
      uploadQueueRef.current = [];
      totalFilesCountRef.current = 0;
      completedFilesCountRef.current = 0;

      batchUploadFilesToAPI(filesToUpload);
    }
  }, [batchUploadFilesToAPI]);

  React.useEffect(() => {
    // 在 effect 内部保存 ref 值，供 cleanup 函数使用
    const timeoutId = apiCallTimeoutRef.current;
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // ==================== 文件类型感知的上传处理 ====================
  const customRequest = async ({ file, onSuccess, onError }: any) => {
    try {
      // 获取文件名
      const fileName = (file as File).name;

      // 增加总文件计数
      totalFilesCountRef.current++;

      // 上传文件到OSS
      const url = await srmUploadService.uploadFile(file as File);

      // 将文件加入批量上传队列
      uploadQueueRef.current.push({
        url,
        fileName,
        onSuccess: (response: any) => {
          // 必须调用onSuccess，否则Upload组件状态不会更新
          onSuccess?.(response);
        },
      });

      // 检查是否可以执行批量API调用
      checkAndExecuteBatchUpload();
    } catch (err: any) {
      completedFilesCountRef.current++;
      checkAndExecuteBatchUpload();
      onError?.(err);
    }
  };

  return (
    <div>
      {!readOnly && (
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <span style={{ fontWeight: 500 }}>{label}：</span>
          <Form.Item
            name={fieldName}
            valuePropName="fileList" // 必须
            getValueFromEvent={(e) => {
              return e.fileList.map((file: UploadFile) => {
                return {
                  ...file,
                  url: file.response?.url || file.url,
                  fileId: file.response?.fileId, // 保留从customRequest返回的fileId
                };
              });
            }}
            style={{ margin: 0 }}
          >
            <Upload
              {...uploadProps}
              customRequest={customRequest}
              onChange={onFileChange(fieldName, form)}
            >
              <Button
                icon={<UploadOutlined />}
                variant="outlined"
                color="primary"
              >
                {t("点击上传")}
              </Button>
            </Upload>
          </Form.Item>
        </div>
      )}

      {readOnly && (
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <span style={{ fontWeight: 500 }}>{label}：</span>
        </div>
      )}

      {/* 自定义文件列表：预览 + 下载 + 删除 */}
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue, setFieldsValue }) => {
          const fileList = form.getFieldValue(fieldName);
          if (!fileList) return null;

          const removeFile = (url?: string) => {
            if (readOnly) return; // 只读模式下不允许删除
            setFieldsValue({
              [fieldName]: fileList.filter((p: any) => p.url !== url),
            });
          };

          return (
            <div style={{ marginBottom: "12px" }}>
              {fileList.map((file: UploadFile, index: number) => (
                <FileListItem
                  key={`${fieldName}-${file.uid || file.url || ""}-${index}`}
                  file={file}
                  hiddenRemove={readOnly}
                  remove={removeFile}
                />
              ))}
            </div>
          );
        }}
      </Form.Item>
    </div>
  );
};

/**
 * 生成动态证照文件表单项
 * 始终为所有文件类型（营业执照、银行信息、收款授权信、开户证明、其他）生成表单项
 * 如果 profileList 中包含了某个文件类型的数据，则显示已上传的文件；否则显示空的上传表单
 *
 * 重要：每个文件类型只生成一个表单项。如果 profileList 中有多个同类型的文件，
 * 它们会被合并到同一个表单项中，由 Upload 组件统一管理。
 *
 * 示例：
 * - 无 profileList 时，显示 5 个空的上传字段
 * - 有 profileList 时，按文件类型分组，每类型一个字段，字段值包含该类型的所有文件
 *
 * @param profileList 档案信息列表（可选）
 * @param formRef 表单实例引用，用于更新表单值
 * @returns 证照文件表单项数组
 */
const generateProfileFileFormItems = (
  profileList?: ProfileInfo[],
  formRef?: React.RefObject<FormInstance>,
  readOnly?: boolean,
  t?: any,
) => {
  const translate = t || ((key: string) => key);
  const items: any[] = [];

  // 用于按文件类型分组 profileList 中的文件
  const filesByType: Record<number, ProfileInfo[]> = {};

  // 如果有 profileList，规范化并按文件类型分组
  if (profileList && profileList.length > 0) {
    profileList.forEach((profile, index) => {
      // 规范化 profileInfo（自动补全 fileName）
      const normalizedProfile = enrichProfileInfo(profile);

      const fileType = normalizeFileType(normalizedProfile.fileType);

      if (!FILE_TYPE_MAP[fileType]) {
        return;
      }

      if (!filesByType[fileType]) {
        filesByType[fileType] = [];
      }
      filesByType[fileType].push(normalizedProfile);
    });
  }

  // 遍历所有文件类型，每个类型生成一个表单项
  Object.entries(FILE_TYPE_MAP).forEach(([fileTypeStr, config]) => {
    const fileType = parseInt(fileTypeStr, 10);
    const { label, fieldName } = config;

    // 合并该文件类型的所有文件
    const profilesForType = filesByType[fileType] || [];
    const allFiles: FileItem[] = [];

    profilesForType.forEach((profile) => {
      const files = parseFileUrls(profile.fileUrl);
      allFiles.push(...files);
    });

    // 生成表单项
    items.push({
      name: fieldName,
      col: { span: 24 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: `请上传${label}` }],
      custom: {
        render: (_fieldName: any, _remove: any, fieldValue: any) => {
          return (
            <ProfileFileFormField
              fieldName={fieldName}
              label={label}
              initialFiles={allFiles}
              required={true}
              readOnly={readOnly}
              t={translate}
            />
          );
        },
      },
    });
  });

  return items;
};

/**
 * 生成注册人信息表单项
 * 根据注册人数组动态生成多个注册人块
 *
 * @param registrantList 注册人信息列表
 * @param readOnly 是否为只读模式
 * @param isApplyChangeMode 是否为申请变更模式
 * @param t 翻译函数
 */
const generateRegistrantFormItems = (
  registrantList?: RegistrantInfo[],
  readOnly?: boolean,
  isApplyChangeMode?: boolean,
  t?: any,
) => {
  const translate = t || ((key: string) => key);
  const items: any[] = [];

  if (!registrantList || registrantList.length === 0) {
    return items;
  }

  // 判断注册人信息字段是否禁用
  // 只有 contactDepartment（所属部门）和 contactPhone（电话号码）在申请变更模式下可以编辑
  const isRegistrantFieldDisabled = (fieldName: string) => {
    // 只读模式下全部禁用
    if (readOnly) {
      return true;
    }
    // 在申请变更模式下，只有部门和电话可以编辑
    if (isApplyChangeMode) {
      return !["contactDepartment", "contactPhone"].includes(fieldName);
    }
    // 非申请变更模式下全部禁用
    return true;
  };

  // 遍历注册人列表，为每个注册人生成表单字段
  registrantList.forEach((registrant, index) => {
    const registrantIndex = index + 1;
    const fieldNameSuffix =
      registrantList.length > 1 ? `_${registrantIndex}` : "";

    // 注册人信息标题在主表单配置中已经定义，这里不需要重复添加标题

    // 在非第一个注册人前添加分隔符，实现隔行显示
    if (index > 0) {
      items.push({
        name: `registrantSpacer_${index}`,
        col: { span: 24 },
        custom: {
          render: () => <div style={{ height: "12px" }} />,
        },
      });
    }

    // 注册邮箱
    items.push({
      label: translate("注册邮箱"),
      name: `registrationEmail${fieldNameSuffix}`,
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: "注册邮箱为必填" }],
      widget: {
        widgetType: "Input",
        disabled: isRegistrantFieldDisabled("registrationEmail"),
      },
      initialValue: registrant.email,
    });

    // 姓氏
    items.push({
      label: translate("姓氏"),
      name: `lastName${fieldNameSuffix}`,
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: "姓氏为必填" }],
      widget: {
        widgetType: "Input",
        disabled: isRegistrantFieldDisabled("lastName"),
      },
      initialValue: registrant.surname,
    });

    // 名字
    items.push({
      label: translate("名字"),
      name: `firstName${fieldNameSuffix}`,
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: "名字为必填" }],
      widget: {
        widgetType: "Input",
        disabled: isRegistrantFieldDisabled("firstName"),
      },
      initialValue: registrant.name,
    });

    // 所属部门 - 在申请变更模式下可以编辑
    items.push({
      label: translate("所属部门"),
      name: `contactDepartment${fieldNameSuffix}`,
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: isRegistrantFieldDisabled("contactDepartment"),
      },
      initialValue: registrant.deptCode,
    });

    // 电话号码 - 在申请变更模式下可以编辑
    items.push({
      label: translate("电话号码"),
      name: `contactPhone${fieldNameSuffix}`,
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: "电话号码为必填" }],
      widget: {
        widgetType: "Input",
        disabled: isRegistrantFieldDisabled("contactPhone"),
      },
      initialValue: registrant.phone,
    });
  });

  return items;
};

/**
 * 规范化银行信息数据
 * 将后端返回的银行信息转换为组件使用的格式
 *
 * 规则：
 * - 已有的银行（bank.id 存在）：保留 id，用于编辑/删除操作
 * - 新增的银行（bank.id 不存在）：不设置 id，仅用于显示，提交时不传递 id
 */
const normalizeBankInfo = (backendBankList?: BankInfo[]): BankInfo[] => {
  if (!backendBankList || backendBankList.length === 0) {
    return [];
  }

  return backendBankList.map((bank) => ({
    id: bank.id, // 只保留后端返回的 id，新增数据不设置 id
    accountType: bank.accountType,
    country: bank.country,
    openBankName: bank.openBankName,
    openBankNumber: bank.openBankNumber,
    account: bank.account,
    swiftCode: bank.swiftCode,
    accountHolder: bank.accountHolder,
    openBankInSap: bank.openBankInSap,
  }));
};

// ==================== 第一步表单配置 ====================

/**
 * 供应商准入申请第一步表单配置
 * 包含：申请人信息、供应商基本信息、地址信息、供应商信息、注册人信息、银行信息、证照文件
 *
 * @param registrantList 注册人信息数组（可选），用于动态生成注册人表单项
 * @param bankInfoList 银行信息数组（可选），用于初始化银行信息表格数据
 * @param profileList 档案/证照信息数组（可选），用于动态生成证照文件表单项
 * @param formRef 表单实例引用（可选），用于更新表单值
 * @param readOnly 是否为只读模式（可选）
 * @param isApplyChangeMode 是否为申请变更模式（可选）
 * @param errorBankNumbers SAP验证失败的开户行行号数组，用于在表格中标红（可选）
 */
export const getAdmissionApplicationFormItems = (
  registrantList?: RegistrantInfo[],
  bankInfoList?: BankInfo[],
  profileList?: ProfileInfo[],
  formRef?: React.RefObject<FormInstance>,
  readOnly?: boolean, // 是否为只读模式
  isApplyChangeMode?: boolean, // 是否为申请变更模式
  errorBankNumbers?: string[], // SAP验证失败的开户行行号
  t?: any, // 翻译函数
) => {
  const translate = t || ((key: string) => key);
  const normalizedBankInfo = normalizeBankInfo(bankInfoList);

  // 申请人信息字段：在所有模式下都禁用（除了只读时已经全部禁用）
  const applicantFields = [
    "applicantName",
    "purchaseUserNo",
    "createDate",
    "deptName",
    "postName",
  ];

  // 申请变更模式下额外禁用的字段（供应商基本信息中不能改）
  const additionalDisabledInChangeMode = ["supplierName", "supplierUsName"];

  // 非申请变更模式下禁用的供应商基本信息字段（只读模式除外，已全部禁用）
  const disabledInNormalMode = [
    // 供应商基本信息
    "businessValidity",
    "supplierSocialCreditCode",
    "supplierType",
    "manufacturerName",
    // 地址信息
    "supplierCountry",
    "supplierCity",
    "supplierPostalCode",
    "supplierStreet",
    "supplierLanguage",
  ];

  // 根据字段名和模式判断是否禁用
  const isFieldDisabled = (fieldName: string) => {
    if (readOnly) {
      return true; // 只读模式下所有字段禁用
    }
    // 申请人信息字段在任何编辑模式下都禁用
    if (applicantFields.includes(fieldName)) {
      return true;
    }
    // 在申请变更模式下，供应商名称字段禁用
    if (
      isApplyChangeMode &&
      additionalDisabledInChangeMode.includes(fieldName)
    ) {
      return true;
    }
    // 在非申请变更模式下，供应商基本信息中的某些字段禁用
    if (!isApplyChangeMode && disabledInNormalMode.includes(fieldName)) {
      return true;
    }
    return false;
  };

  return [
    // ==================== 申请人信息 ====================
    {
      name: "applicantSection",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.sectionHeader}>
            <div className={formStyles.sectionIndicator} />
            <span className={formStyles.sectionTitle}>
              {translate("申请人信息")}
            </span>
          </div>
        ),
      },
    },
    {
      name: "applicantWrapperStart",
      col: { span: 24 },
      wrapperStart: { className: formStyles.wrapperPadded },
    },
    {
      label: translate("员工姓名"),
      name: "applicantName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: isFieldDisabled("applicantName"),
      },
    },
    {
      label: translate("员工编号"),
      name: "purchaseUserNo",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: isFieldDisabled("purchaseUserNo"),
      },
    },
    {
      label: translate("创建日期"),
      name: "createDate",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: isFieldDisabled("createDate"),
      },
    },
    {
      label: translate("部门"),
      name: "deptName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: isFieldDisabled("deptName"),
      },
    },
    {
      label: translate("岗位"),
      name: "postName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: isFieldDisabled("postName"),
      },
    },
    {
      label: translate("邮箱"),
      name: "purchaseUserEmail",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [
        { required: true, message: translate("请输入邮箱") },
        { type: "email", message: translate("邮箱格式不正确") },
      ],
      widget: {
        widgetType: "Input",
        placeholder: translate("请输入"),
      },
    },
    {
      name: "applicantWrapperEnd",
      col: { span: 24 },
      wrapperEnd: true,
    },

    // ==================== 供应商基本信息 ====================
    {
      name: "supplierSection",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.sectionHeader}>
            <div className={formStyles.sectionIndicator} />
            <span className={formStyles.sectionTitle}>
              {translate("供应商基本信息")}
            </span>
          </div>
        ),
      },
    },
    {
      name: "supplierWrapperStart",
      col: { span: 24 },
      wrapperStart: { className: formStyles.wrapperLight },
    },
    {
      label: translate("供应商名称（中文）"),
      name: "supplierName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: "供应商名称（中文）为必填" }],
      className: "vertical-label-item",
      widget: {
        widgetType: "Input",
        disabled: true,
      },
    },
    {
      label: translate("供应商名称（英文）"),
      name: "supplierUsName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      className: "vertical-label-item",
      widget: {
        widgetType: "Input",
        disabled: true,
      },
    },
    {
      label: translate("营业有效期"),
      name: "businessValidity",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: "营业有效期为必填" }],
      className: "vertical-label-item",
      widget: {
        widgetType: "DatePicker.RangePicker",
        disabled: isFieldDisabled("businessValidity"),
        placeholder: [translate("开始日期"), translate("结束日期")],
        format: "YYYY-MM-DD",
      },
    },
    {
      label: translate("统一社会信用代码/商业登记号"),
      name: "supplierSocialCreditCode",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      className: "vertical-label-item",
      widget: {
        widgetType: "Input",
        disabled: isFieldDisabled("supplierSocialCreditCode"),
      },
    },
    {
      label: translate("企业类型"),
      name: "supplierType",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: "企业类型为必填" }],
      className: "vertical-label-item",
      widget: {
        widgetType: "Select",
        disabled: isFieldDisabled("supplierType"),
        options: [
          { label: translate("生产商"), value: 1 },
          { label: translate("代理商"), value: 2 },
        ],
      },
    },
    {
      label: translate("生产商名称"),
      name: "manufacturerName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      className: "vertical-label-item",
      isShow: (formValue: any) => formValue?.supplierType === 2,
      widget: {
        widgetType: "Input",
        disabled: isFieldDisabled("manufacturerName"),
      },
    },

    // ==================== 地址信息 ====================
    {
      name: "addressSectionDivider",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.subSectionTitle}>
            {translate("地址信息")}
          </div>
        ),
      },
    },
    {
      label: translate("国家"),
      name: "supplierCountry",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: "国家为必填" }],
      widget: {
        widgetType: "Input",
        disabled: isFieldDisabled("supplierCountry"),
      },
    },
    {
      label: translate("省份/城市"),
      name: "supplierCity",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: "省份/城市为必填" }],
      widget: {
        widgetType: "Input",
        disabled: isFieldDisabled("supplierCity"),
      },
    },
    {
      label: translate("邮编"),
      name: "supplierPostalCode",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: isFieldDisabled("supplierPostalCode"),
      },
    },
    {
      label: translate("街道/门牌号"),
      name: "supplierStreet",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: "街道/门牌号为必填" }],
      widget: {
        widgetType: "Input",
        disabled: isFieldDisabled("supplierStreet"),
      },
    },
    {
      label: translate("语言"),
      name: "supplierLanguage",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: "语言为必填" }],
      className: "vertical-label-item",
      widget: {
        widgetType: "Select",
        disabled: isFieldDisabled("supplierLanguage"),
        options: LANGUAGE_OPTIONS,
      },
    },

    // ==================== 供应商信息 ====================
    {
      name: "supplierInfoSectionDivider",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.subSectionTitle}>
            {translate("供应商信息")}
          </div>
        ),
      },
    },
    {
      label: translate("经营范围"),
      name: "supplyInfo",
      col: { span: 24 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input.TextArea",
        placeholder:
          "计算机软硬件、网络设备及电子商务平台的研究开发、技术咨询、技术转让、技术服务；系统集成、技术转让。（以上各项涉及法律行政法规、国务院决定禁止的项目除外,限制的项目须取得许可后方可经营）...",
        rows: 3,
        style: { resize: "none" },
      },
    },

    // ==================== 注册人信息（动态生成）====================
    {
      name: "contactSectionDivider",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.sectionTitle}>
            {translate("注册人信息")}
          </div>
        ),
      },
    },
    ...generateRegistrantFormItems(
      registrantList,
      readOnly,
      isApplyChangeMode,
      t,
    ),
    // ==================== 银行信息 ====================
    {
      label: translate("银行信息"),
      name: "bankInfoList",
      col: { span: 24 },
      labelCol: { span: 0 },
      wrapperCol: { span: 24 },
      rules: [
        {
          validator: (_, value) => {
            if (!value || value.length === 0) {
              return Promise.reject(translate("请至少添加一条银行信息"));
            }
            return Promise.resolve();
          },
        },
      ],
      initialValue: normalizedBankInfo,
      custom: {
        render: (
          _fieldName: any,
          _remove: any,
          fieldValue: any,
          { formRef }: any,
        ) => (
          <BankInfoTable
            key="bank-info-table"
            value={fieldValue || []}
            onChange={(newValue) => {
              if (formRef?.current) {
                formRef.current.setFieldsValue({ bankInfoList: newValue });
              }
            }}
            readOnly={readOnly}
            errorBankNumbers={errorBankNumbers}
          />
        ),
      },
    },
    // ==================== 证照文件 ====================
    {
      name: "certificateSectionDivider",
      col: { span: 24 },
      custom: {
        render: () => (
          <div
            style={{
              margin: "8px 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div className={formStyles.sectionTitle}>
              {translate("证照文件")}
            </div>
            {/* <Button type="primary" icon={<DownloadOutlined />}>
              {translate("下载全部")}
            </Button> */}
          </div>
        ),
      },
    },
    ...generateProfileFileFormItems(profileList, formRef, readOnly, t),
    {
      name: "supplierWrapperEnd",
      col: { span: 24 },
      wrapperEnd: true,
    },
  ];
};
